package net.bothosting.docs.pages.hosting_in_bot_hosting_net

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initLink_a_domain_to_your_serverPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("hosting_in_bot_hosting_net/link_a_domain_to_your_server.md", FrontMatterElement.Builder { addScalar("description", "This guide is for running an HTTP server that you want to link your domain to, through our reverse proxy."); addScalar("icon", "link-horizontal"); }))
}

@Page("/hosting_in_bot_hosting_net/link-a-domain-to-your-server")
@Layout(".components.layouts.DocsLayout")
@Composable
fun Link_a_domain_to_your_serverPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("link-a-domain-to-your-server") }) {
        org.jetbrains.compose.web.dom.Text("Link a Domain to your Server")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("To begin, you need your numerical server ID, not to be confused with its UUID. The easiest way to get it right now is from your server's Manage Server page, where you can find the ID in the URL.")
    }
    com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figure") {
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("img", attrs = {attr("src", "../.gitbook/assets/image (21).png");attr("alt", "")})
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figcaption")
    }

    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Get your server ID from the URL.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Now that you have your server ID, you simply need to add a CNAME record pointing to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("<your server ID>.site.bot-hosting.cloud")
        }
        org.jetbrains.compose.web.dom.Text(". For example, with my ID, 8787, I would point it to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("8787.site.bot-hosting.cloud")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figure") {
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("img", attrs = {attr("src", "../.gitbook/assets/image (22).png");attr("alt", "")})
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figcaption")
    }

    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Example setup in Cloudflare.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You can set the name to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@")
        }
        org.jetbrains.compose.web.dom.Text(" to use your root domain without a subdomain.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("getting-a-tls-certificate") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Getting a TLS certificate")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("a", attrs = {attr("href", "#t532mk63r66");attr("id", "t532mk63r66")}) {
        }
    }
    com.varabyte.kobweb.silk.components.display.Callout(type = com.varabyte.kobweb.silk.components.display.CalloutType.NOTE, label = null, variant = com.varabyte.kobweb.silk.components.display.CalloutDefaults.Variant) {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text(" If you want to visit your site over HTTPS, you need a TLS certificate.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We recommend Cloudflare for getting a TLS certificate. It is free and works great. First, if you have not already done so, move your domain's nameservers to Cloudflare by signing up at ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://dash.cloudflare.com/") {
            org.jetbrains.compose.web.dom.Text("https://dash.cloudflare.com")
        }
        org.jetbrains.compose.web.dom.Text(" and setting it up.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Then you need to set up a CNAME record, as above, but this time make sure the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Proxy status")
        }
        org.jetbrains.compose.web.dom.Text(" option is checked.")
    }
    com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figure") {
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("img", attrs = {attr("src", "../.gitbook/assets/image (23).png");attr("alt", "")})
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figcaption")
    }

    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("DNS record with proxying enabled.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("But this alone will not work. Since you are now passing the connection through a proxy, we cannot figure out what target you are connecting to, so you also need to add a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("TXT")
        }
        org.jetbrains.compose.web.dom.Text(" record. This TXT record should contain ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("bh-server=<your server ID>")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figure") {
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("img", attrs = {attr("src", "../.gitbook/assets/image (24).png");attr("alt", "")})
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figcaption")
    }

    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A TXT record that specifies which server to target.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Make sure you have SSL/TLS Encryption Mode set to Flexible. You should then be able to visit your domain over HTTPS in your browser.")
    }
    com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figure") {
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("img", attrs = {attr("src", "../.gitbook/assets/image (25).png");attr("alt", "")})
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figcaption")
    }

}
